import React from 'react';
import { createPortal } from 'react-dom';
import {
  TextInput, Platform, NativeSyntheticEvent, TextInputKeyPressEventData,
} from 'react-native';
import {
  HStack, Box, Icon, Pressable, SearchIcon, Text, VStack, Divider,
} from '@gluestack-ui/themed';
import debounce from 'lodash/debounce';
import { CircleX } from 'lucide-react-native';
import log from '@/common/utils/logger';
import type { SearchFilter, SearchWithFiltersProps } from './DataTable/types';

/**
 * Parse search text into structured filters and remaining plain text.
 * Supports field:value and field:"value with spaces" syntax.
 */
const parseSearchFilters = (searchText: string | null, searchableFields: string[] = []) => {
  const filters: SearchFilter[] = [];
  let plainText = '';

  if (!searchText) return { filters, plainText };

  const regex = /(\w+(?:\.\w+)*):"([^"]+)"|(\w+(?:\.\w+)*):(\S+)/g;
  let lastIndex = 0;
  let match = regex.exec(searchText);

  while (match !== null) {
    const beforeMatch = searchText.slice(lastIndex, match.index).trim();
    if (beforeMatch) {
      plainText += (plainText ? ' ' : '') + beforeMatch;
    }

    const field = match[1] || match[3];
    const value = match[2] || match[4];

    if (field && value && searchableFields.includes(field)) {
      filters.push({ field, value });
    } else {
      plainText += (plainText ? ' ' : '') + match[0];
    }

    lastIndex = regex.lastIndex;
    match = regex.exec(searchText);
  }

  const remaining = searchText.slice(lastIndex).trim();
  if (remaining) {
    plainText += (plainText ? ' ' : '') + remaining;
  }

  return { filters, plainText };
};

/**
 * Build search text string from filters array and plain text.
 * Validates filter values to prevent malformed query strings.
 */
const buildSearchText = (filters: SearchFilter[], plainText: string) => {
  const filterParts = filters
    .filter((f) => {
      // Skip invalid filters (empty values or trailing colons)
      if (!f.value || f.value.trim() === '' || f.value.endsWith(':')) {
        log.warn(`[SearchWithFilters] Skipping invalid filter: ${f.field}:${f.value}`);
        return false;
      }
      return true;
    })
    .map((f) => {
      const needsQuotes = f.value.includes(' ');
      return needsQuotes ? `${f.field}:"${f.value}"` : `${f.field}:${f.value}`;
    });
  return [...filterParts, plainText].filter(Boolean).join(' ');
};

/** Compact chip showing an active filter */
const FilterChip = ({
  field,
  value,
  onRemove,
  isDisabled,
}: {
  field: string;
  value: string;
  onRemove: () => void;
  isDisabled?: boolean;
}) => (
  <HStack
    bg="$bgBrandSubtle"
    borderRadius="$full"
    px="$2"
    py="$0.5"
    mr="$1"
    alignItems="center"
    space="xs"
    flexShrink={0}
    borderWidth={1}
    borderColor="$borderBrand"
  >
    <Text size="2xs" color="$textBrand" fontWeight="$medium">
      {field}
      :
    </Text>
    <Text size="2xs" color="$textHeading" fontWeight="$bold" maxWidth={100} numberOfLines={1}>
      {value}
    </Text>
    <Pressable
      onPress={onRemove}
      disabled={isDisabled}
      p="$0.5"
      borderRadius="$full"
    // eslint-disable-next-line react-native/no-inline-styles
      sx={{ ':hover': { bg: '$bgHover' } }}
    >
      <Icon as={CircleX} size="2xs" color="$brandBase" />
    </Pressable>
  </HStack>
);

/** Single suggestion row in the autocomplete dropdown */
const SuggestionItem = ({
  field,
  plainText,
  onSelect,
  isHighlighted,
}: {
  field: string;
  plainText: string;
  onSelect: (field: string, value: string) => void;
  isHighlighted: boolean;
}) => (
  <Pressable
    onPress={() => onSelect(field, plainText)}
    p="$3"
    bg={isHighlighted ? '$bgHover' : '$bgApp'}
  // eslint-disable-next-line react-native/no-inline-styles
    sx={{ ':hover': { bg: '$bgSubtle' } }}
  >
    <HStack space="md" alignItems="center">
      <Box w={24} h={24} borderRadius="$full" bg="$bgBrandSubtle" alignItems="center" justifyContent="center">
        <Text size="2xs" fontWeight="$bold" color="$brandBase">
          {field.charAt(0).toUpperCase()}
        </Text>
      </Box>
      <VStack>
        <Text size="sm" fontWeight="$medium" color="$textHeading">
          {field}
          :
          {' '}
          <Text color="$textSubtle" fontWeight="$normal">
            {plainText}
          </Text>
        </Text>
        <Text size="2xs" color="$textHint">
          Filter by
          {' '}
          {field}
        </Text>
      </VStack>
    </HStack>
  </Pressable>
);

/** Autocomplete dropdown panel - renders via Portal to escape container constraints */
const SuggestionsDropdown = ({
  suggestions,
  highlightedIndex,
  onSelect,
  plainText,
  defaultSearchField,
  onSearchDefault,
  position,
  dropdownRef,
}: {
  suggestions: string[];
  highlightedIndex: number;
  onSelect: (field: string, value: string) => void;
  plainText: string;
  defaultSearchField: string;
  onSearchDefault: () => void;
  position: { top: number; left: number; width: number };
  dropdownRef: React.RefObject<React.ComponentRef<typeof Box>>;
}) => {
  const dropdownContent = (
    <Box
      ref={dropdownRef}
      data-search-dropdown
      // @ts-expect-error - fixed is supported on web
      position="fixed"
      top={position.top}
      left={position.left}
      width={position.width}
      bg="$bgApp"
      borderWidth={1}
      borderColor="$borderLight"
      borderRadius="$lg"
      py="$1"
      zIndex={99999}
      // eslint-disable-next-line react-native/no-inline-styles
      sx={{
        elevation: 4,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
      }}
    >
      <Box px="$3" py="$1">
        <Text size="2xs" color="$textHint" fontWeight="$medium" textTransform="uppercase">
          Filter by field:
        </Text>
      </Box>
      {suggestions.map((field, index) => (
        <SuggestionItem
          key={field}
          field={field}
          plainText={plainText}
          onSelect={onSelect}
          isHighlighted={index === highlightedIndex}
        />
      ))}
      <Divider mt="$1" />
      {/* eslint-disable-next-line react-native/no-inline-styles */}
      <Pressable onPress={onSearchDefault} p="$3" sx={{ ':hover': { bg: '$bgSubtle' } }}>
        <HStack space="sm" alignItems="center">
          <Icon as={SearchIcon} size="xs" color="$textSubtle" />
          <Text size="sm" color="$textBody">
            Search &quot;
            {plainText}
            &quot; in
            {' '}
            {defaultSearchField}
          </Text>
        </HStack>
      </Pressable>
    </Box>
  );

  if (typeof document === 'undefined' || Platform.OS !== 'web') {
    return null; // Portal only works on web for document.body
  }

  return createPortal(dropdownContent, document.body);
};

/**
 * SearchWithFilters - A search input with inline filter chips and autocomplete.
 *
 * Features:
 * - Parses field:value syntax into visual chips.
 * - Shows autocomplete suggestions for available fields based on input.
 * - Supports complex queries with multiple field filters and free text.
 * - Keyboard navigation (arrows, enter, escape, backspace).
 * - Portal-based dropdown to ensure visibility over complex layouts.
 *
 * @param {SearchWithFiltersProps} props - Configuration for search behavior and suggestions.
 * @returns {React.ReactElement} The search input component.
 */
const SearchWithFilters: React.FC<SearchWithFiltersProps> = ({
  onSearch,
  searchPlaceholder = 'Search...',
  isDisabled = false,
  searchableFields = [],
  defaultSearchField = 'title',
  testID = 'search-input',
}) => {
  const [filters, setFilters] = React.useState<SearchFilter[]>([]);
  const [plainText, setPlainText] = React.useState('');
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0, width: 0 });
  const [invalidFieldError, setInvalidFieldError] = React.useState<string | null>(null);
  const inputRef = React.useRef<TextInput>(null);
  const containerRef = React.useRef<React.ComponentRef<typeof Box>>(null);
  const dropdownRef = React.useRef<React.ComponentRef<typeof Box>>(null);

  // Update dropdown position when showing suggestions
  const updateDropdownPosition = React.useCallback(() => {
    if (containerRef.current && Platform.OS === 'web') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rect = (containerRef.current as any).getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Current combined search value for triggers and UI logic
  const searchValue = React.useMemo(() => buildSearchText(filters, plainText), [filters, plainText]);

  // Generate suggestions based on input
  const suggestions = React.useMemo(() => {
    if (!plainText || plainText.length < 1 || plainText.includes(':')) return [];
    const lowerInput = plainText.toLowerCase();
    return searchableFields
      .filter((field) => field.toLowerCase().includes(lowerInput) || lowerInput.length >= 2)
      .slice(0, 6);
  }, [plainText, searchableFields]);

  const debouncedSearch = React.useMemo(() => debounce((query: string) => onSearch?.(query), 2000), [onSearch]);

  const triggerSearch = React.useCallback(
    (query: string) => {
      debouncedSearch.cancel();
      onSearch?.(query);
    },
    [onSearch, debouncedSearch],
  );

  const commitPlainTextAsFilters = React.useCallback(() => {
    const { filters: newFoundFilters, plainText: remainingText } = parseSearchFilters(plainText, searchableFields);

    if (newFoundFilters.length > 0) {
      const updatedFilters = [...filters, ...newFoundFilters];
      setFilters(updatedFilters);
      setPlainText(remainingText);
      setInvalidFieldError(null); // Clear any error
      const newSearch = buildSearchText(updatedFilters, remainingText);
      triggerSearch(newSearch);
      return true;
    }

    // Check if user tried to use an invalid field (contains colon but no valid filter was created)
    if (plainText.includes(':') && !plainText.startsWith('"') && newFoundFilters.length === 0) {
      const fieldAttempt = plainText.split(':')[0];
      if (fieldAttempt && !searchableFields.includes(fieldAttempt)) {
        setInvalidFieldError(
          `Invalid field "${fieldAttempt}". Valid fields: ${searchableFields.join(', ')}`,
        );
        log.warn(
          `[SearchWithFilters] Invalid field name: "${fieldAttempt}". Valid fields: ${searchableFields.join(', ')}`,
        );
        // Keep the text as-is so user can correct it
        return false;
      }
    }

    setInvalidFieldError(null);
    return false;
  }, [plainText, filters, searchableFields, triggerSearch]);

  const handleSelectSuggestion = (field: string, value: string) => {
    // Validate value before creating filter (reject empty or trailing colon)
    if (!value || value.trim() === '' || value.endsWith(':')) {
      log.warn(`[SearchWithFilters] Invalid filter value for ${field}: "${value}"`);
      return;
    }

    // Create a new filter with the field and the typed value
    const newFilters = [...filters, { field, value }];
    setFilters(newFilters);
    setPlainText('');
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    triggerSearch(buildSearchText(newFilters, ''));
    inputRef.current?.focus();
  };

  const handleRemoveFilter = (index: number) => {
    const newFilters = filters.filter((_, i) => i !== index);
    setFilters(newFilters);
    triggerSearch(buildSearchText(newFilters, plainText));
  };

  const clearSearch = () => {
    setFilters([]);
    setPlainText('');
    setShowSuggestions(false);
    debouncedSearch.cancel();
    onSearch?.('');
  };

  const handleTextChange = (text: string) => {
    setPlainText(text);
    setInvalidFieldError(null); // Clear error when user types
    setShowSuggestions(true);
    setHighlightedIndex(-1);
    updateDropdownPosition();
    debouncedSearch(buildSearchText(filters, text));
  };

  const handleKeyPress = ({ nativeEvent }: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    const { key } = nativeEvent;
    if (key === 'Enter') {
      if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        handleSelectSuggestion(suggestions[highlightedIndex], plainText);
      } else {
        if (!commitPlainTextAsFilters()) {
          triggerSearch(searchValue);
        }
        setShowSuggestions(false);
      }
    } else if (key === 'ArrowDown') {
      setHighlightedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (key === 'ArrowUp') {
      setHighlightedIndex((prev) => Math.max(prev - 1, -1));
    } else if (key === 'Escape') {
      setShowSuggestions(false);
    } else if (key === 'Backspace' && !plainText && filters.length > 0) {
      handleRemoveFilter(filters.length - 1);
    }
  };

  // Close suggestions on outside click (accounting for portal)
  React.useEffect(() => {
    if (typeof document === 'undefined' || Platform.OS !== 'web') return () => {};

    const handleClickOutside = (e: MouseEvent) => {
      // Check if click is inside the search container or the dropdown
      const target = e.target as HTMLElement;
      if ((containerRef.current as unknown as HTMLElement)?.contains(target)) return;
      if ((dropdownRef.current as unknown as HTMLElement)?.contains(target)) return;
      setShowSuggestions(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [updateDropdownPosition]);

  const showDropdown = !!(showSuggestions && suggestions.length > 0 && plainText && !plainText.includes(':'));

  return (
    <Box ref={containerRef} position="relative">
      {/* Search container with chips */}
      <HStack
        bg="$bgApp"
        borderWidth={isFocused ? 2 : 1}
        borderColor={invalidFieldError ? '$error' : isFocused ? '$borderBrand' : '$borderLight'}
        borderRadius="$lg"
        alignItems="center"
        flex={1}
        minWidth={200}
        h={44}
        px="$3"
        testID="search-input-container"
      >
        <Icon as={SearchIcon} size="sm" color="$textHint" mr="$2" />
        {/* Filter chips */}
        {filters.map((f, index) => (
          <FilterChip
            key={`${f.field}-${f.value}`}
            field={f.field}
            value={f.value}
            onRemove={() => handleRemoveFilter(index)}
            isDisabled={isDisabled}
          />
        ))}

        {/* Text input */}
        <TextInput
          ref={inputRef}
          testID={testID}
          value={plainText}
          onChangeText={handleTextChange}
          onKeyPress={handleKeyPress}
          onFocus={() => {
            setIsFocused(true);
            updateDropdownPosition();
            setShowSuggestions(true);
          }}
          onBlur={() => setIsFocused(false)}
          placeholder={filters.length > 0 ? 'Add more...' : searchPlaceholder || 'Search'}
          editable={!isDisabled}
          // eslint-disable-next-line react-native/no-inline-styles
          style={[
            {
              flex: 1,
              minWidth: 80,
              height: 36,
              borderWidth: 0,
              backgroundColor: 'transparent',
              fontSize: 14,
              color: '#344054', // $textBody
            },
            Platform.OS === 'web' && {
              outlineStyle: 'none',
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ] as any}
        />

        {/* Clear button */}
        {!!searchValue && (
          <Pressable
            onPress={clearSearch}
            p="$1"
            borderRadius="$full"
            // eslint-disable-next-line react-native/no-inline-styles
            sx={{ ':hover': { bg: '$bgHover' } }}
            testID="clear-search-button"
            disabled={isDisabled}
            flexShrink={0}
          >
            <Icon as={CircleX} size="sm" color="$textSubtle" />
          </Pressable>
        )}
      </HStack>

      {/* Autocomplete dropdown - rendered via portal */}
      {showDropdown && (
        <SuggestionsDropdown
          suggestions={suggestions}
          highlightedIndex={highlightedIndex}
          onSelect={handleSelectSuggestion}
          plainText={plainText}
          defaultSearchField={defaultSearchField}
          position={dropdownPosition}
          dropdownRef={dropdownRef}
          onSearchDefault={() => {
            if (!commitPlainTextAsFilters()) {
              triggerSearch(searchValue);
            }
            setShowSuggestions(false);
          }}
        />
      )}

      {/* Error message below search box */}
      {invalidFieldError && (
        <Box mt="$1" px="$2">
          <Text size="xs" color="$error">
            {invalidFieldError}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default SearchWithFilters;
